import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { addDaysYmd, localParts, zonedTime, addMonthsYmd, monthGrid, type StatusTone } from '@rydres/shared';
import { Button, Notice, fmtTime, MonthGrid } from '@rydres/ui';
import { driverApi } from '../lib/api.js';
import { ProfileLoading, useProfile } from '../lib/profile.js';
import type { Profile } from '../lib/types.js';

type Mode = 'day' | 'week' | 'month';
type Kind = 'ride' | 'request' | 'done' | 'off' | 'personal';
interface Item {
  id: string;
  kind: Kind;
  start: number;
  end: number;
  title: string;
  allDay?: boolean;
  to?: string;
}

const HOUR_PX = 44;
const LOOK: Record<Kind, string> = {
  ride: 'bg-accent text-on-accent',
  request: 'border border-warn bg-warn-soft text-warn',
  done: 'bg-line text-ink2',
  off: 'border border-dashed border-muted bg-card2 text-muted',
  personal: 'border border-line bg-card2 text-ink2',
};
const LEGEND: Array<[Kind, string]> = [
  ['ride', 'Ride'],
  ['request', 'Request'],
  ['off', 'Time off'],
  ['personal', 'Your calendar'],
];
const KIND_OF: Record<string, Kind> = { confirmed: 'ride', requested: 'request', quoted: 'request', completed: 'done', no_show: 'done' };

const iso = (ms: number) => new Date(ms).toISOString();
const span = (i: Item, tz: string) => (i.allDay ? 'All day' : `${fmtTime(iso(i.start), tz)}–${fmtTime(iso(i.end), tz)}`);

/** Side-by-side columns for items that overlap, so a clash with a personal appointment is visible. */
function columns(items: Item[]): Array<Item & { col: number; cols: number }> {
  const out: Array<Item & { col: number; cols: number }> = [];
  let group: Array<Item & { col: number; cols: number }> = [];
  let groupEnd = -Infinity;
  const flush = () => {
    const ends: number[] = [];
    for (const it of group) {
      let c = ends.findIndex((e) => e <= it.start);
      if (c === -1) c = ends.push(0) - 1;
      ends[c] = it.end;
      it.col = c;
    }
    for (const it of group) it.cols = ends.length;
    out.push(...group);
    group = [];
  };
  for (const it of [...items].sort((a, b) => a.start - b.start || b.end - a.end)) {
    if (group.length && it.start >= groupEnd) {
      flush();
      groupEnd = -Infinity;
    }
    group.push({ ...it, col: 0, cols: 1 });
    groupEnd = Math.max(groupEnd, it.end);
  }
  if (group.length) flush();
  return out;
}

/**
 * Rides, requests, time off and the driver's own appointments together, by day or by week,
 * in the driver's time zone. Tapping a free slot on the day view blocks that time.
 */
export function CalendarView({ initialDay }: { initialDay?: string }) {
  const { profile } = useProfile();
  // Today and the day boundaries are the driver's, so wait for their time zone.
  if (!profile) return <ProfileLoading />;
  return <Calendar profile={profile} initialDay={initialDay} />;
}

function Calendar({ profile, initialDay }: { profile: Profile; initialDay?: string }) {
  const tz = profile.timezone;
  const today = localParts(new Date(), tz).ymd;
  const [day, setDay] = useState(initialDay && /^\d{4}-\d{2}-\d{2}$/.test(initialDay) ? initialDay : today);
  const [mode, setMode] = useState<Mode>('day');
  const [items, setItems] = useState<Item[] | null>(null);
  const [linked, setLinked] = useState<number | null>(null);
  const [pick, setPick] = useState<number | null>(null);
  // A month shows whole weeks, so it starts and ends where the grid does.
  const weeks = monthGrid(day);
  const start = mode === 'month' ? weeks[0]![0]! : day;
  const days = mode === 'day' ? 1 : mode === 'week' ? 7 : weeks.length * 7;
  const step = (by: number) => setDay(mode === 'month' ? addMonthsYmd(day, by) : addDaysYmd(day, by * days));

  const load = useCallback(async () => {
    const from = zonedTime(start, 0, tz).getTime();
    const to = zonedTime(addDaysYmd(start, days), 0, tz).getTime();
    const q = { from: iso(from), to: iso(to) };
    try {
      const [b, bl, ev] = await Promise.all([
        // From half a day earlier, so a ride that runs past midnight still shows.
        driverApi.bookings({ from: iso(from - 12 * 3_600_000), to: q.to, status: 'requested,quoted,confirmed,completed,no_show' }),
        driverApi.blocks(q),
        driverApi.calendarBusy(q),
      ]);
      const all: Item[] = [
        ...b.bookings.map((x) => ({ id: x.id, kind: KIND_OF[x.status] ?? 'done', start: Date.parse(x.startsAt), end: Date.parse(x.endsAt), title: x.riderName, to: `/bookings/${x.id}` })),
        ...bl.blocks.map((x) => ({ id: x.id, kind: 'off' as const, start: Date.parse(x.startsAt), end: Date.parse(x.endsAt), title: x.reason || 'Time off' })),
        ...ev.events.map((x) => ({ id: x.id, kind: 'personal' as const, start: Date.parse(x.startsAt), end: Date.parse(x.endsAt), title: x.title || 'Busy', allDay: x.allDay })),
      ];
      setItems(all.filter((i) => i.end > from && i.start < to));
    } catch {
      setItems([]);
    }
  }, [start, days, tz]);
  useEffect(() => void load(), [load]);
  useEffect(() => {
    driverApi.calendarImports().then((r) => setLinked(r.imports.length), () => setLinked(null));
  }, []);

  const label = (ymd: string, opts: Intl.DateTimeFormatOptions) =>
    zonedTime(ymd, 12 * 60, tz).toLocaleDateString('en-US', { timeZone: tz, ...opts });
  const title =
    mode === 'month'
      ? label(day, { month: 'long', year: 'numeric' })
      : mode === 'day'
        ? `${day === today ? 'Today, ' : ''}${label(day, { weekday: 'short', month: 'short', day: 'numeric' })}`
        : `${label(day, { month: 'short', day: 'numeric' })} – ${label(addDaysYmd(day, 6), { month: 'short', day: 'numeric' })}`;
  // Rides and requests make the dots; time off and personal appointments show on the day and week views.
  const marks: Record<string, StatusTone[]> = {};
  for (const i of items ?? [])
    if (i.kind === 'ride' || i.kind === 'request' || i.kind === 'done') (marks[localParts(new Date(i.start), tz).ymd] ??= []).push(i.kind === 'ride' ? 'ok' : i.kind === 'request' ? 'warn' : 'muted');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <button onClick={() => step(-1)} aria-label="Earlier" className="rounded-full bg-card2 px-3 py-2 font-bold">‹</button>
          <button onClick={() => step(1)} aria-label="Later" className="rounded-full bg-card2 px-3 py-2 font-bold">›</button>
          {day !== today && <button onClick={() => setDay(today)} className="rounded-full bg-card2 px-3 py-2 text-sm font-bold">Today</button>}
        </div>
        <div className="flex gap-1 rounded-full bg-card2 p-1 text-sm font-bold">
          {(['day', 'week', 'month'] as const).map((m) => (
            <button key={m} onClick={() => setMode(m)} className={`rounded-full px-3 py-1.5 capitalize ${mode === m ? 'bg-card shadow-card' : 'text-muted'}`}>{m}</button>
          ))}
        </div>
      </div>
      <div className="text-lg font-black">{title}</div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted">
        {LEGEND.map(([k, name]) => (
          <span key={k} className="flex items-center gap-1"><span className={`inline-block h-3 w-3 rounded ${LOOK[k]}`} />{name}</span>
        ))}
      </div>

      {items === null ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : mode === 'month' ? (
        <MonthGrid month={day} today={today} marks={marks} onDay={(d) => { setDay(d); setMode('day'); }} />
      ) : mode === 'day' ? (
        <DayTimeline day={day} tz={tz} items={items} hours={profile.hours} isToday={day === today} onPick={setPick} />
      ) : (
        <WeekAgenda start={day} tz={tz} items={items} hours={profile.hours} label={label} onDay={(d) => { setDay(d); setMode('day'); }} />
      )}

      <Link to="/bookings/availability" className="flex items-center justify-between rounded-2xl bg-card2 px-4 py-3 text-sm font-bold">
        <span>
          Hours, time off and phone calendar
          <span className="block text-xs font-medium text-muted">
            {linked === null ? ' ' : linked > 0 ? `${linked} phone calendar${linked === 1 ? '' : 's'} linked` : 'Link your phone calendar so its appointments block bookings'}
          </span>
        </span>
        <span className="text-muted">›</span>
      </Link>
      {pick !== null && <BlockSheet start={pick} tz={tz} onDone={() => { setPick(null); void load(); }} onCancel={() => setPick(null)} />}
    </div>
  );
}

function DayTimeline({ day, tz, items, hours, isToday, onPick }: {
  day: string;
  tz: string;
  items: Item[];
  hours: Array<{ from: string; to: string } | null> | null;
  isToday: boolean;
  onPick: (start: number) => void;
}) {
  const dayStart = zonedTime(day, 0, tz).getTime();
  const dayEnd = zonedTime(addDaysYmd(day, 1), 0, tz).getTime();
  const whole = (i: Item) => i.allDay || (i.start <= dayStart && i.end >= dayEnd);
  const banners = items.filter(whole);
  const timed = items.filter((i) => !whole(i));
  const minsOf = (t: number) => (Math.min(dayEnd, Math.max(dayStart, t)) - dayStart) / 60_000;
  // 6 AM to 10 PM, stretched to fit anything earlier or later.
  const first = Math.min(6, ...timed.map((i) => Math.floor(minsOf(i.start) / 60)));
  const last = Math.max(22, ...timed.map((i) => Math.ceil(minsOf(i.end) / 60)));
  const y = (mins: number) => ((mins - first * 60) * HOUR_PX) / 60;
  const weekday = localParts(new Date(dayStart + 12 * 3_600_000), tz).weekday;
  const open = hours?.[weekday];
  const toMin = (hhmm: string) => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
  // Shaded: times outside the pickup hours for this weekday.
  const shade: Array<[number, number]> = !hours
    ? []
    : !open
      ? [[first * 60, last * 60]]
      : toMin(open.from) <= toMin(open.to)
        ? [[first * 60, toMin(open.from)], [toMin(open.to), last * 60]]
        : [[toMin(open.to), toMin(open.from)]];
  // The "now" line only where the grid shows that time: at 1 AM the day starts at 6 and it would land on the header.
  const nowMins = isToday ? minsOf(Date.now()) : null;
  const now = nowMins !== null && nowMins >= first * 60 && nowMins <= last * 60 ? nowMins : null;

  return (
    <div className="space-y-2">
      {hours && !open && <p className="text-xs text-muted">No pickups on this weekday (your hours).</p>}
      {banners.map((b) => (
        <div key={b.id} className={`rounded-xl px-3 py-2 text-sm font-bold ${LOOK[b.kind]}`}>All day · {b.title}</div>
      ))}
      <div
        className="relative cursor-pointer select-none rounded-2xl border border-line bg-card"
        style={{ height: (last - first) * HOUR_PX }}
        onClick={(e) => {
          const top = e.currentTarget.getBoundingClientRect().top;
          const mins = first * 60 + ((e.clientY - top) / HOUR_PX) * 60;
          onPick(dayStart + Math.floor(mins / 30) * 30 * 60_000);
        }}
      >
        {shade.map(([a, b]) =>
          b > a ? <div key={`${a}-${b}`} className="absolute inset-x-0 bg-card2/80" style={{ top: y(Math.max(a, first * 60)), height: y(Math.min(b, last * 60)) - y(Math.max(a, first * 60)) }} /> : null,
        )}
        {Array.from({ length: last - first }, (_, i) => (
          <div key={i} className="absolute inset-x-0 border-t border-line" style={{ top: i * HOUR_PX }}>
            <span className="absolute left-2 top-0.5 text-[11px] text-muted">
              {new Date(Date.UTC(2000, 0, 1, first + i)).toLocaleTimeString('en-US', { hour: 'numeric', timeZone: 'UTC' })}
            </span>
          </div>
        ))}
        {now !== null && <div className="absolute left-12 right-0 z-10 border-t-2 border-danger" style={{ top: y(now) }} />}
        <div className="absolute inset-y-0 left-14 right-1">
          {columns(timed).map((i) => {
            const top = y(minsOf(i.start));
            const height = Math.max(22, y(minsOf(i.end)) - top - 2);
            const style = { top, height, left: `${(i.col / i.cols) * 100}%`, width: `calc(${100 / i.cols}% - 2px)` };
            const body = (
              <>
                <div className="truncate font-bold">{i.title}</div>
                {height > 34 && <div className="truncate opacity-80">{span(i, tz)}</div>}
              </>
            );
            const cls = `absolute overflow-hidden rounded-lg px-2 py-1 text-xs ${LOOK[i.kind]}`;
            return i.to ? (
              <Link key={i.id} to={i.to} className={cls} style={style} onClick={(e) => e.stopPropagation()}>{body}</Link>
            ) : (
              <div key={i.id} className={cls} style={style} onClick={(e) => e.stopPropagation()}>{body}</div>
            );
          })}
        </div>
      </div>
      <p className="text-center text-xs text-muted">Tap an empty time to block it.</p>
    </div>
  );
}

function WeekAgenda({ start, tz, items, hours, label, onDay }: {
  start: string;
  tz: string;
  items: Item[];
  hours: Array<{ from: string; to: string } | null> | null;
  label: (ymd: string, opts: Intl.DateTimeFormatOptions) => string;
  onDay: (ymd: string) => void;
}) {
  return (
    <div className="space-y-2">
      {Array.from({ length: 7 }, (_, n) => {
        const ymd = addDaysYmd(start, n);
        const s = zonedTime(ymd, 0, tz).getTime();
        const e = zonedTime(addDaysYmd(ymd, 1), 0, tz).getTime();
        const list = items.filter((i) => i.end > s && i.start < e).sort((a, b) => a.start - b.start);
        const off = hours && !hours[localParts(new Date(s + 12 * 3_600_000), tz).weekday];
        return (
          <section key={ymd} className="rounded-2xl border border-line bg-card p-3">
            <button onClick={() => onDay(ymd)} className="flex w-full items-baseline justify-between text-left">
              <span className="font-bold">{label(ymd, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
              <span className="text-xs text-muted">{list.length ? '' : off ? 'No pickups' : 'Free'} ›</span>
            </button>
            {list.length > 0 && (
              <ul className="mt-2 space-y-1">
                {list.map((i) => {
                  const row = (
                    <span className="flex items-center gap-2 text-sm">
                      <span className={`inline-block h-3 w-3 shrink-0 rounded ${LOOK[i.kind]}`} />
                      <span className="w-32 shrink-0 text-xs text-muted">{span(i, tz)}</span>
                      <span className="truncate">{i.title}</span>
                    </span>
                  );
                  return <li key={i.id}>{i.to ? <Link to={i.to}>{row}</Link> : row}</li>;
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

const LENGTHS: Array<[number, string]> = [
  [30, '30 min'],
  [60, '1 hour'],
  [120, '2 hours'],
  [240, '4 hours'],
];

/** Blocks time from a tapped slot; riders cannot book over it. */
function BlockSheet({ start, tz, onDone, onCancel }: { start: number; tz: string; onDone: () => void; onCancel: () => void }) {
  const [minutes, setMinutes] = useState(60);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const end = start + minutes * 60_000;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await driverApi.addBlock({ startsAt: iso(start), endsAt: iso(end), reason: null });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not block that time.');
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-scrim p-4 sm:items-center" onClick={onCancel}>
      <div className="w-full max-w-md space-y-3 rounded-3xl border border-line bg-card p-5" onClick={(e) => e.stopPropagation()}>
        <div className="text-lg font-black">Block this time?</div>
        <p className="text-sm text-ink2">
          {new Date(start).toLocaleDateString('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' })}, {fmtTime(iso(start), tz)} to {fmtTime(iso(end), tz)}. Riders cannot book over it.
        </p>
        <div className="grid grid-cols-4 gap-1 rounded-2xl bg-card2 p-1 text-sm font-bold">
          {LENGTHS.map(([m, name]) => (
            <button key={m} onClick={() => setMinutes(m)} className={`rounded-xl px-2 py-2 ${minutes === m ? 'bg-card shadow-card' : 'text-muted'}`}>{name}</button>
          ))}
        </div>
        {error && <Notice tone="danger">{error}</Notice>}
        <div className="flex gap-2">
          <Button variant="ghost" className="flex-1" onClick={onCancel}>Cancel</Button>
          <Button className="flex-1" disabled={busy} onClick={() => void save()}>Block time</Button>
        </div>
      </div>
    </div>
  );
}
